import type { Game } from '../core/game';
import { trackGateEvent } from '../telemetry/events';
import { getInstallId } from '../telemetry/identity';
import { currentWorkerBase, formatCodeInput, normalizeCodeInput, redeemUnlockCode, type RedeemRefusal } from '../telemetry/unlock';
import { getEntitlement } from './entitlement';
import { writeEntitlement } from './entitlement/store';
import { claimGate, setEntitled } from './gameApi';
import { toast } from './toast';

/**
 * Formulaire « J'ai un code » (A112, v0.55.3) partagé par l'écran d'achat et
 * le menu Réglages de l'Accalmie : un ami qui reçoit un code au niveau 1 doit
 * pouvoir le saisir sans attendre le boss du niveau 2. Même client, mêmes
 * messages, même télémétrie que l'écran d'achat.
 *
 * Sélecteurs : `[data-role="code-input"]`, `[data-action="code-submit"]`,
 * `[data-role="code-error"]`.
 */

export const CODE_MESSAGES: Record<RedeemRefusal | 'malformed', string> = {
  malformed: 'Un code a 12 lettres et chiffres.',
  invalid: 'Code inconnu.',
  used: 'Ce code a déjà été utilisé sur un autre appareil.',
  rate: 'Trop d’essais, réessayez dans une heure.',
  unavailable: 'Vérification impossible pour l’instant (hors ligne ?).',
  network: 'Vérification impossible pour l’instant (hors ligne ?).',
};

/** Ouverture par code : le module d'entitlement s'il est installé, sinon le même effet à la main. */
export function applyCodeUnlock(game: Game, token: string): boolean {
  const api = getEntitlement();
  if (api) return api.unlockWith('code', token);
  writeEntitlement({ source: 'code', token, at: new Date().toISOString() });
  setEntitled(game, true);
  return claimGate(game);
}

export function unlockCodeFormHtml(idPrefix: string): string {
  return `
    <div class="unlock-code-form" data-role="unlock-code-form">
      <input type="text" class="f-input gate-code-input" data-role="code-input" inputmode="text" autocapitalize="characters" autocomplete="off" autocorrect="off" spellcheck="false" maxlength="14" placeholder="XXXX-XXXX-XXXX" aria-label="Code de déblocage" aria-describedby="${idPrefix}-code-error">
      <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="code-submit">Valider</button>
      <p class="gate-error" id="${idPrefix}-code-error" data-role="code-error" role="alert" hidden></p>
    </div>`;
}

export interface UnlockCodeFormOptions {
  /** Après un code accepté (le jeu est déjà ouvert et le niveau dû réclamé). */
  onUnlocked?: (claimed: boolean) => void;
}

/** Branche le formulaire rendu par `unlockCodeFormHtml` ; renvoie le démontage. */
export function mountUnlockCodeForm(root: HTMLElement, game: Game, opts: UnlockCodeFormOptions = {}): () => void {
  const input = root.querySelector<HTMLInputElement>('[data-role="code-input"]');
  const submit = root.querySelector<HTMLButtonElement>('[data-action="code-submit"]');
  const error = root.querySelector<HTMLElement>('[data-role="code-error"]');
  if (!input || !submit || !error) return () => {};
  let busy = false;
  const showError = (message: string): void => {
    error.textContent = message;
    error.hidden = message === '';
  };
  const onInput = (): void => {
    const formatted = formatCodeInput(input.value);
    if (formatted !== input.value) input.value = formatted;
    if (!error.hidden) showError('');
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void onSubmit();
    }
  };
  async function onSubmit(): Promise<void> {
    if (busy) return;
    const normalized = normalizeCodeInput(input!.value);
    if (!normalized) {
      showError(CODE_MESSAGES.malformed);
      return;
    }
    busy = true;
    submit!.disabled = true;
    submit!.setAttribute('aria-busy', 'true');
    showError('');
    try {
      const out = await redeemUnlockCode(normalized, { installId: getInstallId(), base: currentWorkerBase() });
      if (out.ok) {
        trackGateEvent('code_ok', { detail: 'code', level: game.state.level });
        toast('Code accepté : jeu complet débloqué.');
        const claimed = applyCodeUnlock(game, out.token);
        opts.onUnlocked?.(claimed);
      } else {
        trackGateEvent('code_refused', { detail: out.reason, level: game.state.level });
        showError(CODE_MESSAGES[out.reason]);
      }
    } finally {
      busy = false;
      if (submit!.isConnected) {
        submit!.disabled = false;
        submit!.setAttribute('aria-busy', 'false');
      }
    }
  }
  const onClick = (): void => void onSubmit();
  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKey);
  submit.addEventListener('click', onClick);
  return () => {
    input.removeEventListener('input', onInput);
    input.removeEventListener('keydown', onKey);
    submit.removeEventListener('click', onClick);
  };
}
